// A controller's input and its state, both plain data. The state is JSON-safe (the kernel saves it as a component and
// hashes it); the input is what the game read from its actions this step, already turned into world space.
import type { Vec3 } from "@rpg/collision";
import type { XZ } from "./yaw";

/** A movement mode's name. Ground, air and glide are built; climb, swim and mounted are slots a game fills. */
export type ModeId = "ground" | "air" | "glide" | "climb" | "swim" | "mounted" | (string & {});

/** One step of input for a controller. */
export interface ControllerInput {
  /** The wish direction on the ground plane in world space, length 0..1 (the stick's tilt). See `stickToWorld`. */
  move: XZ;
  /** Jump held, and pressed this step (true in one step per press). */
  jump: boolean;
  jumpPressed: boolean;
  /** Sprint as a held state (hold or toggle is the input layer's business). */
  sprint: boolean;
  /** Glide held, and pressed this step. */
  glide: boolean;
  glidePressed: boolean;
  /** A move override (combat's lunge, a dodge's roll): this step on the ground the controller moves at this horizontal
   *  velocity (m/s, world space) and turns to `yaw` when given, in place of the stick. Absent or null: the stick. */
  drive?: MoveDrive | null;
  /** Multipliers the game sets this step (absent: 1): `move` on the ground speeds (a slow status, a buff), `glideSink`
   *  on the glide's sink speed and `glideSpeed` on its horizontal speeds (a longer scarf carries the glide further). */
  scale?: { move?: number; glideSink?: number; glideSpeed?: number } | null;
}

/** What a move asks the controller for, one step at a time (see `ControllerInput.drive`). */
export interface MoveDrive {
  x: number;
  z: number;
  /** Face this way (radians, the kernel's convention); absent: keep the facing. */
  yaw?: number;
}

/** No input: what a controller that doesn't have control gets. */
export const NO_INPUT: Readonly<ControllerInput> = Object.freeze({ move: Object.freeze({ x: 0, z: 0 }), jump: false, jumpPressed: false, sprint: false, glide: false, glidePressed: false });

/** The water a controller stands in. */
export interface WaterContact {
  surface: number;
  /** How deep the feet are under the surface (m). */
  depth: number;
}

/** A controller's whole state. Plain JSON: save it, hash it, restore it. */
export interface ControllerState {
  /** The bottom of the capsule (m). The body sits at feet + halfHeight + radius. */
  feet: Vec3;
  /** Velocity (m/s). */
  vel: Vec3;
  /** Facing, radians (the kernel's convention: 0 faces +Z). */
  yaw: number;
  /** The current movement mode, and how long it has run (s). */
  mode: ModeId;
  modeTime: number;
  /** Standing on something walkable after the last move. */
  grounded: boolean;
  /** Time since the last grounded step (s): 0 on the ground. */
  sinceGrounded: number;
  /** Airborne from a jump (no coyote jump then), and the jump still held (the variable height). */
  jumped: boolean;
  jumpHeld: boolean;
  /** Time since an unspent jump press (s), or -1 for none: the jump buffer. */
  buffer: number;
  /** A jump to take off with on the next step (a buffered press met the ground). */
  jumpQueued: boolean;
  /** Turning in place before setting off. */
  pivot: boolean;
  /** Sprint held last step, and the sprint burst's time left (s). */
  sprinting: boolean;
  burst: number;
  /** The surface tag underfoot, and the body it belongs to (null in the air or on untagged ground). */
  surface: string | null;
  ground: string | null;
  /** The water the feet are in, or null. */
  water: WaterContact | null;
  /** The controllable this one rides (a mount, a boat), set by a control handoff; null on foot. */
  riding: string | null;
}

/** A state at rest, standing at `feet` facing `yaw`. */
export function restState(feet: Vec3, yaw = 0): ControllerState {
  return {
    feet: { ...feet },
    vel: { x: 0, y: 0, z: 0 },
    yaw,
    mode: "ground",
    modeTime: 0,
    grounded: true,
    sinceGrounded: 0,
    jumped: false,
    jumpHeld: false,
    buffer: -1,
    jumpQueued: false,
    pivot: false,
    sprinting: false,
    burst: 0,
    surface: null,
    ground: null,
    water: null,
    riding: null,
  };
}

/** Horizontal speed (m/s). */
export function groundSpeed(s: ControllerState): number {
  return Math.hypot(s.vel.x, s.vel.z);
}
